import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { speechModelsEndpoints } from '@/api/endpoints';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';
import { getProviderOptions } from '@/modules/developerTools/pages/speechModels/helper';
import {
  CreateSpeechModel,
  ElevenLabsRegion,
  SpeechModel,
  SpeechModelProvider,
  UpdateSpeechModel,
} from '@/modules/developerTools/types';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

export interface SpeechModelFormValues {
  name: string;
  provider: SpeechModelProvider | '';
  api_key: string;
  region: ElevenLabsRegion | '';
}

/** A new speech model needs every field; an edit sends only what changed, and a blank key keeps the stored one. */
export const toSpeechModelRequest = (
  { name, provider, api_key, region }: SpeechModelFormValues,
  speechModel?: SpeechModel
): CreateSpeechModel | UpdateSpeechModel => {
  const config = {
    ...(api_key && { api_key }),
    ...(provider === 'elevenlabs' && region && { region }),
  };
  if (!speechModel) {
    return { name, provider: provider as SpeechModelProvider, config: { api_key, ...config } };
  }
  const hasConfigChange = !!api_key || region !== (speechModel.config.region ?? '');
  return { speech_model_id: speechModel.speech_model_id, name, ...(hasConfigChange && { config }) };
};

/** The create and edit form of a speech model. */
export const useSpeechModelForm = (speechModel: SpeechModel | undefined, onClose: () => void) => {
  const { t } = useTranslation();
  const { speechModels, providers, createSpeechModel, updateSpeechModel } = useSpeechModels();
  const isEdit = !!speechModel;

  const schema = yup.object({
    name: createNameFieldSchema({
      getEndpoint: speechModelsEndpoints.checkName,
      excludeName: speechModel?.name,
    }),
    provider: yup.string().required(() => t('validation.required')),
    api_key: isEdit
      ? yup.string().trim()
      : yup
          .string()
          .trim()
          .required(() => t('validation.required')),
    region: yup.string(),
  });

  const form = useForm<SpeechModelFormValues>({
    mode: 'onTouched',
    resolver: createYupResolver<SpeechModelFormValues>(schema),
    defaultValues: {
      name: speechModel?.name ?? '',
      provider: speechModel?.provider ?? '',
      api_key: '',
      region: speechModel?.config.region ?? '',
    },
  });
  const provider = useWatch({ control: form.control, name: 'provider' });

  const onSubmit = async (values: SpeechModelFormValues) => {
    const request = toSpeechModelRequest(values, speechModel);
    if ('speech_model_id' in request) {
      await updateSpeechModel(request);
    } else {
      await createSpeechModel(request);
    }
    onClose();
  };

  return {
    form,
    onSubmit,
    isEdit,
    providerOptions: getProviderOptions(providers, speechModels),
    showRegion: provider === 'elevenlabs',
  };
};
